/**
 * SL (section leader) post-section survey document type and Firebase path.
 *
 * Defines the path and document shape for section leader survey responses.
 * Shared between webapp and functions. Ref/converters live in firebase/client and firebase/admin.
 */

import {z} from 'zod';

import {
  parseSectionId,
  parseSectionWeekId,
  type SectionId,
  type SectionWeekId,
} from '../../../section/sectionApi';
import type {CourseId, UserId} from '../../../types/branded';
import {FirestoreTimestampSchema} from '../../types';
import {
  SurveyResponseValueSchema,
  type SurveyResponseValue,
} from './postSectionSurvey';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

// ============================================================================
// Schema (Zod)
// ============================================================================

/**
 * Per-student understanding rating written by the SL debrief
 * ([#5038](https://github.com/CodeinPlace/cip/issues/5038)). Stored in its own
 * top-level map keyed by student `userId`, parallel to `participation`, and
 * deliberately NOT inside `responses`, so the AI-feedback and moderation loops
 * that iterate `responses` ignore it.
 */
export const StudentTwistUnderstandingSchema = z.object({
  /**
   * The rubric rating the SL gave this student for the week. Zero means the
   * SL marked the rating as not applicable.
   */
  rating: z.number(),
});

/** Per-student understanding rating. See {@link StudentTwistUnderstandingSchema}. */
export type StudentTwistUnderstanding = z.infer<
  typeof StudentTwistUnderstandingSchema
>;

/** One twist turn, as the submitted debrief records it. */
export const DebriefTurnSchema = z.object({
  /** Zero-based position of the turn in the section. */
  index: z.number(),
  /** The twist's title, when one was chosen. */
  twistTitle: z.string().optional(),
  /** The twist's id in the twist library, when one was chosen. */
  twistId: z.string().optional(),
  /**
   * Whether the student drove the turn or navigated it; absent on debriefs
   * submitted before it was recorded.
   */
  role: z.enum(['driver', 'navigator']).optional(),
});

/** One twist turn. See {@link DebriefTurnSchema}. */
export type DebriefTurn = z.infer<typeof DebriefTurnSchema>;

/** The stayed and camera marks the SL gave a typed-in guest. */
export const DebriefGuestParticipationSchema = z.object({
  /** They stayed for the whole section. */
  stayed: z.boolean(),
  /** They had their camera on for most of the section. */
  camera: z.boolean(),
});

/** A typed-in guest's marks. See {@link DebriefGuestParticipationSchema}. */
export type DebriefGuestParticipation = z.infer<
  typeof DebriefGuestParticipationSchema
>;

/**
 * Someone the SL typed in during section, with no account, as the submitted
 * debrief records them. Their marks live here rather than in the
 * `participation` and `studentTwistUnderstanding` maps, which are keyed by
 * account `userId`.
 */
export const DebriefGuestSchema = z.object({
  /** Their id in the week's live session roster. */
  id: z.string(),
  /** The name as typed. */
  name: z.string(),
  /** Their turns, in order. */
  turns: z.array(DebriefTurnSchema),
  /**
   * Whether they stayed and had their camera on. Absent when the SL
   * recorded none, including debriefs submitted before guests were marked.
   */
  participation: DebriefGuestParticipationSchema.optional(),
  /** Their twist rating. Absent when the SL gave none. */
  twistUnderstanding: StudentTwistUnderstandingSchema.optional(),
});

/** A typed-in guest. See {@link DebriefGuestSchema}. */
export type DebriefGuest = z.infer<typeof DebriefGuestSchema>;

/**
 * Schema for SL post-section survey Firestore document.
 * Stored at:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs/{userId}`
 */
export const SLPostSectionSurveyDocumentSchema = z
  .object({
    latestSubmission: FirestoreTimestampSchema.optional(),
    participation: z
      .record(z.string(), z.record(z.string(), z.boolean()))
      .optional(),
    /**
     * Per-student understanding ratings, keyed by student `userId`. See
     * {@link StudentTwistUnderstandingSchema}. Absent on debriefs written
     * before the rating existed.
     */
    studentTwistUnderstanding: z
      .record(z.string(), StudentTwistUnderstandingSchema)
      .optional(),
    /**
     * The typed-in guests the live session listed at submit. Absent on
     * debriefs submitted before it was recorded.
     */
    guests: z.array(DebriefGuestSchema).optional(),
    /**
     * Each student's twist turns from the live session at submit, keyed by
     * student `userId`; covers every driver, rated or not. Absent on
     * debriefs submitted before it was recorded.
     */
    turnsByUid: z.record(z.string(), z.array(DebriefTurnSchema)).optional(),
    responses: z.record(z.string(), SurveyResponseValueSchema).optional(),
    sectionId: z
      .string()
      .optional()
      .refine(s => s === undefined || parseSectionId(s) != null, {
        message: 'Invalid sectionId',
      })
      .transform((s): SectionId | undefined => parseSectionId(s)),
    sectionWeek: z
      .number()
      .optional()
      .refine(n => n === undefined || parseSectionWeekId(n) != null, {
        message: 'Invalid sectionWeekId',
      })
      .transform((n): SectionWeekId | undefined => parseSectionWeekId(n)),
    userId: z
      .string()
      .optional()
      .transform((s): UserId | undefined => s),
    /**
     * Denormalized course identifier. Optional for back-compat with debriefs
     * written before this field was tracked; new writes always include it.
     * Enables `collectionGroup` queries filtered by course (used by the admin
     * dashboard's SL self-rating histogram aggregation). Firestore rules
     * enforce that the stamped value matches the `{courseId}` path segment so
     * an SL cannot spoof their debrief into a different course's stats.
     */
    courseId: z.string().optional(),
  })
  .loose();

/** Section leader post-section survey Firestore document. */
export type SLPostSectionSurveyDocument = z.infer<
  typeof SLPostSectionSurveyDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the SL debrief / post-section survey collection.
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs`
 */
export function getSLPostSectionSurveyCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SL_DEBRIEFS_SUBCOLLECTION,
  });
}

/**
 * Constructs the Firestore document path for an SL post-section survey.
 * Path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slDebriefs/{userId}`
 */
export function getSLPostSectionSurveyDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: UserId;
}): string {
  return `${getSLPostSectionSurveyCollectionPath({courseId, sectionId, sectionWeek})}/${userId}`;
}

// ============================================================================
// Response type and builder
// ============================================================================

/** Question ID to response value for SL survey (same shape as student survey responses). */
export type SLPostSectionSurveyResponse = Record<string, SurveyResponseValue>;

/** Participation state: per-user map of participation keys to booleans. */
export type SLParticipationState = Record<UserId, Record<string, boolean>>;

/** Builds an SL post-section survey document from structured data. */
export function createSLPostSectionSurveyDocument(data: {
  responses: SLPostSectionSurveyResponse;
  userId: UserId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  participation: SLParticipationState;
  courseId: CourseId;
}): SLPostSectionSurveyDocument {
  return {
    responses: data.responses,
    userId: data.userId,
    sectionId: data.sectionId,
    sectionWeek: data.sectionWeek,
    participation: data.participation,
    courseId: data.courseId,
  };
}
